import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  MdAdd,
  MdArrowBack,
  MdCheckCircle,
  MdDeleteOutline,
  MdEdit,
  MdOutlineInventory2,
  MdPayments,
  MdRefresh,
  MdRoute,
  MdStar,
} from 'react-icons/md';
import { RangeDatepicker } from 'chakra-dayzed-datepicker';
import { format, startOfToday } from 'date-fns';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageShell from 'components/layout/PageShell';
import AiAssistantDrawer from 'domains/ai/components/AiAssistantDrawer';
import {
  consumeClientListingEditPrefill,
} from 'domains/ai/utils/storage';
import {
  UnifiedEmptyState,
  UnifiedLoadingState,
  UnifiedSurfaceCard,
  useUnifiedSurfaceStyles,
} from 'components/layout/UnifiedPrimitives';
import { useUnifiedTabsStyles } from 'components/layout/useUnifiedUiStyles';
import DocumentList from 'components/documents/DocumentList';
import Map from 'components/map/components/StaticMap';
import ListingImageGallery from 'domains/listing/components/ListingImageGallery';
import ListingItemsSection, { getItemCount } from 'domains/listing/components/ListingItemsSection';
import ListingLoaCard from 'domains/listing/components/ListingLoaCard';
import PaymentPanel from 'domains/listing/components/PaymentPanel';
import TransportPlanCard from 'domains/listing/components/TransportPlanCard';
import {
  acceptQuote,
  cancelListing,
  completeListing as completeListingService,
  downloadInvoice,
  getListingById,
  getQuotesForListing,
  publishListingDraft,
  rejectQuote,
  updateListing,
  uploadListingImage,
} from '../services/listingService';
import StatusBadge from '../components/StatusBadge';
import { useAuth } from 'contexts/AuthContext';
import { useTranslation } from 'i18n/TranslationProvider';
import { translateRaw } from 'i18n/globalTranslator';
import ListingMessagesPanel from 'domains/collaboration/components/ListingMessagesPanel';
import { getUnreadMessageCount } from 'domains/collaboration/services/collaborationService';

const ACCENT = 'brand.500';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateRange(from, to) {
  if (!from && !to) return translateRaw('Flexible dates');
  if (from && to && from !== to) return `${formatDate(from)} – ${formatDate(to)}`;
  return formatDate(from || to);
}

function daysLeft(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatMoney(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return `€${numeric.toFixed(2)}`;
}

function formatDistanceKm(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? `${numeric} km` : null;
}

function getTransportStage(status) {
  switch (String(status || '').toLowerCase()) {
    case 'draft':
      return translateRaw('Draft');
    case 'accepted':
      return translateRaw('Awaiting payment');
    case 'awaiting_payment':
      return translateRaw('Awaiting payment');
    case 'paid':
      return translateRaw('Awaiting planning');
    case 'planned':
    case 'in_transit':
      return translateRaw('Transport planned');
    case 'pickedup':
      return translateRaw('Picked up');
    case 'delivered':
      return translateRaw('Delivered');
    case 'completed':
      return translateRaw('Completed');
    case 'canceled':
      return translateRaw('Canceled');
    case 'expired':
      return translateRaw('Expired');
    default:
      return translateRaw('Open for quotes');
  }
}

function getQuoteAmounts(quotes) {
  return quotes
    .map((quote) => {
      const netPrice = quote.netPrice ?? quote._netPrice ?? quote.price ?? null;
      const vatRate = quote.vatRate ?? quote._vatRate ?? 0;
      const vat = netPrice != null ? (netPrice * vatRate) / 100 : null;
      const grossPrice =
        quote.grossPrice ??
        quote._grossPrice ??
        quote.totalPrice ??
        (netPrice != null && vat != null ? netPrice + vat : null) ??
        quote.amount ??
        quote.price ??
        null;
      return Number(grossPrice);
    })
    .filter((value) => Number.isFinite(value));
}

function MetricChip({ label, value, icon }) {
  const { subtleBg, subtleBorder, mutedText } = useUnifiedSurfaceStyles();
  const titleColor = useColorModeValue('navy.800', 'white');

  return (
    <HStack
      spacing={3}
      px={4}
      py={3}
      borderRadius="18px"
      border="1px solid"
      borderColor={subtleBorder}
      bg={subtleBg}
      minW={{ base: 'calc(50% - 8px)', md: 'auto' }}
      flex="1"
    >
      <Flex
        w={{ base: '42px', md: '46px' }}
        h={{ base: '42px', md: '46px' }}
        borderRadius="16px"
        align="center"
        justify="center"
        bg="whiteAlpha.700"
        color={ACCENT}
        flexShrink={0}
        boxShadow="0 8px 18px rgba(15, 23, 42, 0.08)"
      >
        <Icon as={icon} boxSize={{ base: 7, md: 8 }} />
      </Flex>
      <Box minW={0}>
        <Text fontSize="10px" fontWeight="800" color={mutedText} textTransform="uppercase" letterSpacing="0.08em">
          {label}
        </Text>
        <Text fontSize="sm" fontWeight="800" color={titleColor} noOfLines={1}>
          {value}
        </Text>
      </Box>
    </HStack>
  );
}

function SectionHeader({ eyebrow, title, subtitle, action = null }) {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const titleColor = useColorModeValue('navy.800', 'white');

  return (
    <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={3} flexWrap="wrap">
      <Box>
        {eyebrow ? (
          <Text fontSize="10px" fontWeight="800" color={mutedColor} textTransform="uppercase" letterSpacing="0.08em">
            {eyebrow}
          </Text>
        ) : null}
        <Text fontSize="lg" fontWeight="900" color={titleColor}>
          {title}
        </Text>
        {subtitle ? (
          <Text fontSize="sm" color={mutedColor} mt={1}>
            {subtitle}
          </Text>
        ) : null}
      </Box>
      {action}
    </Flex>
  );
}

function RouteStopCard({ label, address, windowLabel }) {
  const { subtleBg, subtleBorder, mutedText } = useUnifiedSurfaceStyles();
  const titleColor = useColorModeValue('navy.800', 'white');
  const { t } = useTranslation();

  return (
    <Box
      p={5}
      borderRadius="20px"
      bg={subtleBg}
      border="1px solid"
      borderColor={subtleBorder}
    >
      <Text fontSize="10px" fontWeight="800" color={ACCENT} textTransform="uppercase" letterSpacing="0.08em" mb={2}>
        {label}
      </Text>
      <Text fontSize="md" fontWeight="800" color={titleColor}>
        {address || '—'}
      </Text>
      <Text fontSize="sm" color={mutedText} mt={2}>
        {windowLabel || t('Flexible dates')}
      </Text>
    </Box>
  );
}

function DetailFact({ label, value, accent = false }) {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const titleColor = useColorModeValue('navy.800', 'white');

  if (!value) return null;

  return (
    <Box>
      <Text fontSize="10px" fontWeight="800" color={mutedColor} textTransform="uppercase" letterSpacing="0.08em" mb={1}>
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="800" color={accent ? ACCENT : titleColor}>
        {value}
      </Text>
    </Box>
  );
}

function WorkflowPanel({ listing, acceptedQuote, onPaymentComplete, onComplete, completing }) {
  const { subtleBg, subtleBorder, mutedText } = useUnifiedSurfaceStyles();
  const titleColor = useColorModeValue('navy.800', 'white');
  const successBg = useColorModeValue('green.50', 'green.900');
  const successBorder = useColorModeValue('green.100', 'green.700');
  const completeBg = useColorModeValue('teal.50', 'teal.900');
  const completeBorder = useColorModeValue('teal.100', 'teal.700');
  const { t } = useTranslation();
  const status = String(listing.status || '').toLowerCase();

  let title = t('Listing is live');
  let description = t('Transport providers can review your shipment and send offers.');
  let toneBg = subtleBg;
  let toneBorder = subtleBorder;
  let action = null;

  if (status === 'draft') {
    title = t('Listing is a draft');
    description = t('This listing is not yet published. Complete the details and publish it to start receiving quotes.');
  } else if (status === 'accepted' || status === 'awaiting_payment') {
    if (listing.isPaymentDelayed === true) {
      title = t('Monthly contract — no payment required');
      description = t('This listing is part of your monthly contract. Your account will be invoiced at the end of the month.');
      toneBg = successBg;
      toneBorder = successBorder;
    } else {
      title = t('Payment required');
      description = listing.allowsQuotes === false
        ? t('Complete the payment below to lock the fixed-price acceptance and let the partner start planning.')
        : t('Complete the payment below to lock the selected quote and let the partner start planning.');
      action = (
        <PaymentPanel
          listing={listing}
          acceptedQuote={acceptedQuote}
          onPaymentComplete={onPaymentComplete}
        />
      );
    }
  } else if (status === 'paid') {
    if (listing.isPaymentDelayed === true) {
      title = t('Monthly contract — no payment required');
      description = t('This listing is part of your monthly contract. Your account will be invoiced at the end of the month.');
    } else {
      title = t('Waiting for transport planning');
      description = t('Payment is confirmed. The partner now needs to confirm the truck, driver, and planned dates.');
    }
    toneBg = successBg;
    toneBorder = successBorder;
  } else if (status === 'planned' || status === 'in_transit') {
    title = t('Transport planned');
    description = t('The partner has confirmed the route plan. Pickup and delivery dates are now scheduled.');
  } else if (status === 'pickedup') {
    title = t('Shipment in transit');
    description = t('Pickup is complete and the transport is currently underway.');
  } else if (status === 'delivered') {
    title = t('Delivery confirmation needed');
    description = t('The partner marked the shipment as delivered. Review the final documents and confirm completion.');
    toneBg = successBg;
    toneBorder = successBorder;
    action = (
      <Button
        colorScheme="green"
        borderRadius="xl"
        isLoading={completing}
        onClick={onComplete}
        leftIcon={<Icon as={MdCheckCircle} />}
      >
        {t('Confirm delivery complete')}
      </Button>
    );
  } else if (status === 'completed') {
    title = t('Transport completed');
    description = t('The listing has been fully completed and archived as a finished transport.');
    toneBg = completeBg;
    toneBorder = completeBorder;
  } else if (status === 'canceled') {
    title = t('Listing canceled');
    description = t('This listing is archived and no further transport actions are available.');
  } else if (status === 'expired') {
    title = t('Listing expired');
    description = t('The listing expired before a transport was confirmed.');
  }

  return (
    <UnifiedSurfaceCard p={6}>
      <SectionHeader
        eyebrow={t('Next step')}
        title={title}
        subtitle={description}
      />
      <Box
        mt={5}
        p={4}
        borderRadius="18px"
        bg={toneBg}
        border="1px solid"
        borderColor={toneBorder}
      >
        <Text fontSize="sm" color={titleColor} fontWeight="700">
          {t('Current stage:')} {getTransportStage(status)}
        </Text>
        {listing.partnerPickupDate || listing.partnerDeliveryDate ? (
          <Text mt={2} fontSize="sm" color={mutedText}>
            {t('Planned dates:')} {listing.partnerPickupDate ? formatDate(listing.partnerPickupDate) : '—'} {t('to')}{' '}
            {listing.partnerDeliveryDate ? formatDate(listing.partnerDeliveryDate) : '—'}
          </Text>
        ) : null}
      </Box>
      {action ? <Box mt={5}>{action}</Box> : null}
    </UnifiedSurfaceCard>
  );
}

function StatusSnapshot({ listing, quoteCount }) {
  const titleColor = useColorModeValue('navy.800', 'white');
  const { t } = useTranslation();

  return (
    <UnifiedSurfaceCard p={6}>
      <SectionHeader
        eyebrow={t('Status')}
        title={t('Listing snapshot')}
        subtitle={t('Quick access to the current shipment, quote, and billing state.')}
      />
      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} mt={5}>
        <Box>
          <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
            {t('Current status')}
          </Text>
          <StatusBadge status={listing.status} />
        </Box>
        <DetailFact label={t('Category')} value={listing.category ? listing.category.replace(/_/g, ' ') : null} />
        <DetailFact label={t('Created')} value={formatDate(listing.createdAt)} />
        <DetailFact label={t('Quote count')} value={t('{{count}} quote(s)', { count: quoteCount })} />
        <DetailFact label={t('Transport stage')} value={getTransportStage(listing.status)} />
        {listing.budget != null ? (
          <DetailFact label={t('Budget')} value={formatMoney(listing.budget)} accent />
        ) : (
          <Box />
        )}
      </SimpleGrid>

      {['paid', 'planned', 'in_transit', 'pickedUp', 'delivered', 'completed'].includes(String(listing.status || '')) ? (
        <Button
          mt={5}
          variant="outline"
          colorScheme="blue"
          borderRadius="xl"
          onClick={async () => {
            try {
              const data = await downloadInvoice(listing.id || listing._id);
              if (data?.url) window.open(data.url, '_blank');
            } catch {
              // silent fail
            }
          }}
        >
          {t('Download invoice')}
        </Button>
      ) : null}

      {listing.description ? (
        <>
          <Divider my={5} />
          <Box>
            <Text fontSize="10px" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.08em" mb={1}>
              {t('Shipment notes')}
            </Text>
            <Text fontSize="sm" color={titleColor} lineHeight="tall">
              {listing.description}
            </Text>
          </Box>
        </>
      ) : null}
    </UnifiedSurfaceCard>
  );
}

function DetailsTab({ listing, quotes, acceptedQuote, onPaymentComplete, onComplete, completing }) {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const { t } = useTranslation();
  const fromLabel = [listing.fromAddress, listing.fromCity, listing.fromPostcode, listing.fromCountry].filter(Boolean).join(', ');
  const toLabel = [listing.toAddress, listing.toCity, listing.toPostcode, listing.toCountry].filter(Boolean).join(', ');
  const quoteCount = quotes.length;

  return (
    <Grid templateColumns={{ base: '1fr', xl: '1fr 390px' }} gap={6}>
      <Stack spacing={6} order={{ base: 2, xl: 1 }}>
        <UnifiedSurfaceCard p={{ base: 5, md: 6 }}>
          <SectionHeader
            eyebrow={t('Route')}
            title={t('Pickup to delivery overview')}
            subtitle={t('All route-critical information in one place before you review quotes or documents.')}
          />

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={5}>
            <RouteStopCard
              label={t('Pickup')}
              address={fromLabel}
              windowLabel={formatDateRange(listing.pickupDateFrom, listing.pickupDateTo)}
            />
            <RouteStopCard
              label={t('Delivery')}
              address={toLabel}
              windowLabel={formatDateRange(listing.deliveryDateFrom, listing.deliveryDateTo)}
            />
          </SimpleGrid>

          <Box
            mt={5}
            bg="gray.100"
            borderRadius="22px"
            h={{ base: '250px', md: '360px' }}
            overflow="hidden"
            border="1px solid"
            borderColor={useColorModeValue('gray.100', 'whiteAlpha.100')}
          >
            {listing.fromCoords || listing.toCoords ? (
              <Map
                origin={listing.fromCoords}
                destination={listing.toCoords}
                center={{ lat: 50.5, lng: 10.5 }}
                zoom={6}
                width={800}
                height={400}
                fitBoundsPadding={80}
                maxAutoZoom={12}
                mapContainerStyle={{ width: '100%', height: '100%' }}
              />
            ) : (
              <Flex h="100%" align="center" justify="center" px={6} textAlign="center">
                <Text fontSize="sm" color={mutedColor}>
                  {t('A Google route preview with pins A and B appears here once pickup and delivery coordinates are available.')}
                </Text>
              </Flex>
            )}
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4} mt={5}>
            <DetailFact label={t('Listing reference')} value={listing.humanId || listing.reference} />
            <DetailFact label={t('Pickup window')} value={formatDateRange(listing.pickupDateFrom, listing.pickupDateTo)} />
            <DetailFact label={t('Delivery window')} value={formatDateRange(listing.deliveryDateFrom, listing.deliveryDateTo)} />
            <DetailFact
              label={t('Distance')}
              value={formatDistanceKm(listing.distanceKm ?? listing.route?.distanceKm)}
            />
          </SimpleGrid>
        </UnifiedSurfaceCard>

        {Array.isArray(listing.items) && listing.items.length ? (
          <ListingItemsSection
            items={listing.items}
            category={listing.category}
            subtitle={t('Quantities, dimensions, and handling details ready for transport review.')}
          />
        ) : null}

        <UnifiedSurfaceCard p={5}>
          <SectionHeader
            eyebrow={t('Media')}
            title={t('Shipment images')}
            subtitle={t('Photos uploaded for the shipment and visible across client, partner, and admin views.')}
          />
          <Box mt={4}>
            <ListingImageGallery
              images={listing.images}
              title={t('Listing images')}
              emptyText={t('No shipment images uploaded yet.')}
            />
          </Box>
        </UnifiedSurfaceCard>
      </Stack>

      <Stack spacing={6} order={{ base: 1, xl: 2 }}>
        <WorkflowPanel
          listing={listing}
          acceptedQuote={acceptedQuote}
          onPaymentComplete={onPaymentComplete}
          onComplete={onComplete}
          completing={completing}
        />

        <StatusSnapshot listing={listing} quoteCount={quoteCount} />

        <UnifiedSurfaceCard p={5}>
          <SectionHeader
            eyebrow={t('Transport package')}
            title={t('Plan and authorization')}
            subtitle={t('The partner-confirmed plan and LOA stay grouped together for faster review.')}
          />
          <Stack spacing={4} mt={5}>
            <TransportPlanCard transportPlan={listing.transportPlan} />
            <ListingLoaCard
              listingId={listing.id || listing._id}
              loa={listing.loa}
            />
          </Stack>
        </UnifiedSurfaceCard>

        <UnifiedSurfaceCard p={5}>
          <SectionHeader
            eyebrow={t('Documents')}
            title={t('Transport documents')}
            subtitle={t('Pickup proof and final delivery files uploaded by the transport partner.')}
          />
          <Stack spacing={5} mt={5}>
            <Box>
              <Text fontSize="10px" fontWeight="800" color={mutedColor} textTransform="uppercase" letterSpacing="0.08em" mb={2}>
                {t('Pickup proof')}
              </Text>
              <DocumentList
                entityType="listing"
                entityId={listing.id || listing._id}
                docType="proof_pickup"
                canDelete={false}
              />
            </Box>
            <Box>
              <Text fontSize="10px" fontWeight="800" color={mutedColor} textTransform="uppercase" letterSpacing="0.08em" mb={2}>
                {t('Final delivery documents')}
              </Text>
              <Stack spacing={3}>
                <DocumentList
                  entityType="listing"
                  entityId={listing.id || listing._id}
                  docType="cmr"
                  canDelete={false}
                />
                <DocumentList
                  entityType="listing"
                  entityId={listing.id || listing._id}
                  docType="proof_delivery"
                  canDelete={false}
                />
              </Stack>
            </Box>
          </Stack>
        </UnifiedSurfaceCard>
      </Stack>
    </Grid>
  );
}

function ClientImageManager({ listing, onImagesUpdated }) {
  const toast = useToast();
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const canManageImages = !['accepted', 'completed', 'canceled', 'expired'].includes(
    String(listing.status || '').toLowerCase(),
  );

  const handleImageSelection = async (event) => {
    const files = Array.from(event.target.files || []).slice(0, 10);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!files.length) return;

    setUploading(true);
    try {
      const uploadedImages = [];
      for (const file of files) {
        const result = await uploadListingImage(file, listing.id || listing._id);
        uploadedImages.push({
          documentId: result.documentId,
          fileName: result.fileName,
          gcsPath: result.gcsPath,
        });
      }

      const existingImages = Array.isArray(listing.images) ? listing.images : [];

      await updateListing(listing.id || listing._id, {
        images: [...existingImages, ...uploadedImages],
      });

      toast({
        title: t('Images uploaded'),
        description: t('{{count}} image(s) added to the listing.', { count: uploadedImages.length }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onImagesUpdated();
    } catch (error) {
      toast({
        title: t('Image upload failed'),
        description: error?.response?.data?.message || error.message || t('Unable to upload listing images.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };

  if (!canManageImages) return null;

  return (
    <UnifiedSurfaceCard p={5}>
      <SectionHeader
        eyebrow={t('Update media')}
        title={t('Add more shipment images')}
        subtitle={t('Use fresh photos when the shipment changes or you want to clarify the item condition.')}
        action={
          <>
            <Input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif"
              multiple
              display="none"
              onChange={handleImageSelection}
            />
            <Button
              size="sm"
              colorScheme="blue"
              borderRadius="full"
              onClick={() => fileInputRef.current?.click()}
              isLoading={uploading}
              loadingText={t('Uploading...')}
            >
              {t('Add images')}
            </Button>
          </>
        }
      />
      <Text fontSize="sm" color={mutedColor} mt={3}>
        {t('New images appear immediately in the listing details for client, partner, and admin views.')}
      </Text>
    </UnifiedSurfaceCard>
  );
}

function QuoteCard({ quote, listingId, onAccepted, onRejected }) {
  const toast = useToast();
  const cancelRef = useRef(null);
  const titleColor = useColorModeValue('navy.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const { subtleBg, subtleBorder } = useUnifiedSurfaceStyles();
  const { t } = useTranslation();

  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);

  const partnerName =
    quote.partner?.name ||
    quote.partnerName ||
    quote.createdBy?.name ||
    quote.partner?.displayName ||
    t('Transport Provider');
  const partnerAvatar = quote.partnerAvatar || quote.partner?.avatar || '';
  const rating = quote.partnerRating ?? quote.partner?.rating ?? null;
  const reviewCount = quote.partnerReviewCount ?? quote.partner?.reviewCount ?? 0;

  const netPrice = quote.netPrice ?? quote._netPrice ?? quote.price ?? null;
  const vatRate = quote.vatRate ?? quote._vatRate ?? 0;
  const vat = netPrice != null ? (netPrice * vatRate) / 100 : null;
  const grossPrice =
    quote.grossPrice ??
    quote._grossPrice ??
    quote.totalPrice ??
    (netPrice != null && vat != null ? netPrice + vat : null) ??
    quote.amount ??
    quote.price ??
    null;
  const quoteId = quote._id || quote.id;
  const status = String(quote.status || quote._status || '').toLowerCase();
  const description = quote.message || quote.description || quote._description || '';

  const isAccepted = status === 'accepted';
  const isRejected = status === 'rejected';
  const isExpired = status === 'expired';
  const isPending = status === 'pending';

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await acceptQuote(listingId, quoteId);
      toast({ title: t('Quote accepted'), status: 'success', duration: 2500 });
      setShowAcceptDialog(false);
      onAccepted();
    } catch {
      toast({ title: t('Failed to accept quote'), status: 'error', duration: 3000 });
    } finally {
      setAccepting(false);
    }
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      await rejectQuote(listingId, quoteId);
      toast({ title: t('Quote rejected'), status: 'info', duration: 2500 });
      setShowRejectDialog(false);
      onRejected();
    } catch {
      toast({ title: t('Failed to reject quote'), status: 'error', duration: 3000 });
    } finally {
      setRejecting(false);
    }
  };

  return (
    <>
      <UnifiedSurfaceCard
        p={{ base: 5, md: 6 }}
        borderColor={isAccepted ? 'green.200' : undefined}
        opacity={isRejected ? 0.72 : 1}
      >
        <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'flex-start' }} gap={5}>
          <VStack align="stretch" spacing={4} flex="1">
            <HStack spacing={3} align="flex-start">
              <Avatar name={partnerName} src={partnerAvatar} size="md" />
              <Box minW={0} flex="1">
                <HStack spacing={2} flexWrap="wrap">
                  <Text fontWeight="900" color={titleColor} noOfLines={1}>
                    {partnerName}
                  </Text>
                  <Badge
                    colorScheme={isAccepted ? 'green' : isPending ? 'blue' : isExpired ? 'red' : 'gray'}
                    borderRadius="full"
                    px={3}
                    py={1}
                    textTransform="capitalize"
                  >
                    {status || t('pending')}
                  </Badge>
                </HStack>
                {rating !== null ? (
                  <HStack spacing={1} mt={1}>
                    <Icon as={MdStar} color="yellow.400" boxSize={4} />
                    <Text fontSize="sm" fontWeight="700">{Number(rating).toFixed(1)}</Text>
                    {reviewCount > 0 ? (
                      <Text fontSize="xs" color={mutedColor}>({t('{{count}} reviews', { count: reviewCount })})</Text>
                    ) : null}
                  </HStack>
                ) : null}
              </Box>
            </HStack>

            {description ? (
              <Box
                px={4}
                py={3}
                borderRadius="18px"
                bg={subtleBg}
                border="1px solid"
                borderColor={subtleBorder}
              >
                <Text fontSize="10px" fontWeight="800" color={mutedColor} textTransform="uppercase" letterSpacing="0.08em" mb={1}>
                  {t('Quote message')}
                </Text>
                <Text fontSize="sm" color={titleColor} lineHeight="tall">
                  {description}
                </Text>
              </Box>
            ) : null}

            {(quote.proposedPickupDate || quote.proposedDeliveryDate || quote.vehicleLabel || quote.quoteExpiresAt || quote.sharesTracking) ? (
              <Flex wrap="wrap" gap={2}>
                {quote.proposedPickupDate ? (
                  <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>
                    {t('Pickup:')} {formatDate(quote.proposedPickupDate)}
                  </Badge>
                ) : null}
                {quote.proposedDeliveryDate ? (
                  <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>
                    {t('Delivery:')} {formatDate(quote.proposedDeliveryDate)}
                  </Badge>
                ) : null}
                {quote.vehicleLabel ? (
                  <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
                    {quote.vehicleLabel}
                  </Badge>
                ) : null}
                {quote.quoteExpiresAt ? (
                  <Badge colorScheme={isExpired ? 'red' : 'gray'} borderRadius="full" px={3} py={1}>
                    {t('Expires:')} {formatDate(quote.quoteExpiresAt)}
                  </Badge>
                ) : null}
                {quote.sharesTracking ? (
                  <Badge colorScheme="teal" borderRadius="full" px={3} py={1}>
                    {t('Tracking shared')}
                  </Badge>
                ) : null}
              </Flex>
            ) : null}
          </VStack>

          <Box minW={{ base: '100%', md: '240px' }}>
            <Box
              px={5}
              py={4}
              borderRadius="22px"
              bg={subtleBg}
              border="1px solid"
              borderColor={subtleBorder}
            >
              <Text fontSize="10px" fontWeight="800" color={mutedColor} textTransform="uppercase" letterSpacing="0.08em">
                {t('Total quote')}
              </Text>
              <Text fontSize="2xl" fontWeight="900" color={ACCENT} mt={1}>
                {formatMoney(grossPrice)}
              </Text>
              {netPrice != null ? (
                <Text fontSize="sm" color={mutedColor} mt={1}>
                  {t('Net')} {formatMoney(netPrice)} {vatRate ? `+ ${vatRate}% ${t('VAT')}` : ''}
                </Text>
              ) : null}
              {quote.minNetPrice ? (
                <Text fontSize="sm" color={mutedColor} mt={2}>
                  {t('Minimum net:')} {formatMoney(quote.minNetPrice)}
                </Text>
              ) : null}
            </Box>
          </Box>
        </Flex>

        {isPending ? (
          <HStack mt={5} spacing={3} flexWrap="wrap">
            <Button
              size="sm"
              colorScheme="green"
              borderRadius="lg"
              onClick={() => setShowAcceptDialog(true)}
              isLoading={accepting}
              isDisabled={rejecting}
              loadingText={t('Accepting...')}
            >
              {t('Accept quote')}
            </Button>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              borderRadius="lg"
              onClick={() => setShowRejectDialog(true)}
              isLoading={rejecting}
              isDisabled={accepting}
              loadingText={t('Rejecting...')}
            >
              {t('Reject quote')}
            </Button>
          </HStack>
        ) : null}
      </UnifiedSurfaceCard>

      <AlertDialog
        isOpen={showRejectDialog}
        leastDestructiveRef={cancelRef}
        onClose={() => (rejecting ? null : setShowRejectDialog(false))}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent borderRadius="2xl">
          <AlertDialogHeader fontSize="lg" fontWeight="800">
            {t('Reject quote')}
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={2}>
              {t('Are you sure you want to reject the quote from')} <strong>{partnerName}</strong>?
            </Text>
            <Text fontSize="sm" color="gray.500">
              {t('This action notifies the transport provider and removes the quote from active consideration.')}
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={() => setShowRejectDialog(false)} isDisabled={rejecting}>
              {t('Keep quote')}
            </Button>
            <Button
              colorScheme="red"
              ml={3}
              onClick={handleReject}
              isLoading={rejecting}
              loadingText={t('Rejecting...')}
            >
              {t('Reject quote')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        isOpen={showAcceptDialog}
        leastDestructiveRef={cancelRef}
        onClose={() => (accepting ? null : setShowAcceptDialog(false))}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent borderRadius="2xl">
          <AlertDialogHeader fontSize="lg" fontWeight="800">
            {t('Accept quote')}
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={2}>
              {t("Accept {{partner}}'s quote for {{amount}}?", { partner: partnerName, amount: formatMoney(grossPrice) })}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {t('Other quotes will be declined automatically once this offer is accepted.')}
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={() => setShowAcceptDialog(false)} isDisabled={accepting}>
              {t('Cancel')}
            </Button>
            <Button
              colorScheme="green"
              ml={3}
              onClick={handleAccept}
              isLoading={accepting}
              loadingText={t('Accepting...')}
            >
              {t('Accept quote')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function QuotesTab({ listing, quotes, listingId, onRefresh }) {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const { t } = useTranslation();
  const quoteAmounts = useMemo(() => getQuoteAmounts(quotes), [quotes]);
  const bestQuote = quoteAmounts.length ? Math.min(...quoteAmounts) : null;

  if (quotes.length === 0) {
    return (
      <UnifiedSurfaceCard p={6}>
        <UnifiedEmptyState
          title={listing?.allowsQuotes === false ? t('Waiting for partner acceptance') : t('No quotes yet')}
          description={listing?.allowsQuotes === false
            ? t('Quotes are disabled for this listing. The first partner to accept the fixed price will be assigned automatically.')
            : t('Quotes from transport providers usually begin to appear shortly after publishing. Refresh to check again.')}
          action={
            <Button
              mt={2}
              variant="outline"
              colorScheme="blue"
              size="sm"
              borderRadius="full"
              leftIcon={<Icon as={MdRefresh} />}
              onClick={onRefresh}
            >
              {t('Refresh')}
            </Button>
          }
        />
      </UnifiedSurfaceCard>
    );
  }

  return (
    <Stack spacing={5}>
      <UnifiedSurfaceCard p={5}>
        <Flex align={{ base: 'flex-start', md: 'center' }} justify="space-between" gap={4} flexWrap="wrap">
          <Box>
            <Text fontSize="10px" fontWeight="800" color={mutedColor} textTransform="uppercase" letterSpacing="0.08em">
              {t('Offers summary')}
            </Text>
            <Text fontSize="lg" fontWeight="900">
              {t('{{count}} quote(s) received', { count: quotes.length })}
            </Text>
          </Box>
          <HStack spacing={3} flexWrap="wrap">
            <MetricChip label={t('Best quote')} value={bestQuote != null ? formatMoney(bestQuote) : '—'} icon={MdPayments} />
            <MetricChip label={t('Pending review')} value={`${quotes.filter((quote) => String(quote.status || quote._status || '').toLowerCase() === 'pending').length}`} icon={MdOutlineInventory2} />
          </HStack>
        </Flex>
      </UnifiedSurfaceCard>

      {quotes.map((quote) => (
        <QuoteCard
          key={quote._id || quote.id}
          quote={quote}
          listingId={listingId}
          onAccepted={onRefresh}
          onRejected={onRefresh}
        />
      ))}
    </Stack>
  );
}


// ---------------------------------------------------------------------------
// Item edit form components (category-aware, used inside the edit modal)
// ---------------------------------------------------------------------------

const VEHICLE_CATS = ['cars', 'motorcycles', 'other_vehicles'];
const REMOVAL_CATS = ['removals'];
const PET_CATS = ['pets'];

function defaultItemFor(category) {
  if (VEHICLE_CATS.includes(category)) return { make: '', model: '', status: '', weight: '', quantity: 1 };
  if (REMOVAL_CATS.includes(category)) return { totalVolumeCbm: '', weight: '', originFloor: '', destFloor: '', hasElevatorOrigin: false, hasElevatorDest: false };
  if (PET_CATS.includes(category)) return { petType: '', breed: '', weight: '', petDescription: '', quantity: 1 };
  return { itemDescription: '', weight: '', length: '', width: '', height: '', quantity: 1 };
}

function EditItemCard({ category, item, idx, totalItems, onItemChange, onRemove }) {
  const cardBg = useColorModeValue('gray.50', 'navy.900');
  const { t } = useTranslation();
  return (
    <Box bg={cardBg} borderRadius="xl" p={4}>
      <Flex align="center" justify="space-between" mb={3}>
        <Text fontSize="xs" fontWeight="700">{t('Item {{number}}', { number: idx + 1 })}</Text>
        {totalItems > 1 && (
          <IconButton size="xs" variant="ghost" colorScheme="red" icon={<Icon as={MdDeleteOutline} />} aria-label={t('Remove item')} onClick={onRemove} />
        )}
      </Flex>
      <EditItemForm category={category} item={item} onChange={onItemChange} />
    </Box>
  );
}

function EditItemForm({ category, item, onChange }) {
  const inputBg = useColorModeValue('white', 'navy.800');
  const labelColor = useColorModeValue('gray.700', 'gray.300');
  const { t } = useTranslation();

  if (VEHICLE_CATS.includes(category)) {
    return (
      <Stack spacing={4}>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Make')}</Text>
            <Input size="sm" borderRadius="lg" bg={inputBg} placeholder={t('e.g. BMW')} value={item.make || ''} onChange={(e) => onChange({ ...item, make: e.target.value })} />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Model')}</Text>
            <Input size="sm" borderRadius="lg" bg={inputBg} placeholder={t('e.g. 3 Series')} value={item.model || ''} onChange={(e) => onChange({ ...item, model: e.target.value })} />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Year')}</Text>
            <Input
              size="sm"
              borderRadius="lg"
              bg={inputBg}
              type="number"
              min={1900}
              placeholder="2024"
              value={item.year || ''}
              onChange={(e) => onChange({ ...item, year: e.target.value })}
            />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Condition')}</Text>
            <Select size="sm" borderRadius="lg" bg={inputBg} value={item.vehicleStatus || item.status || ''} onChange={(e) => onChange({ ...item, vehicleStatus: e.target.value, status: e.target.value })}>
              <option value="">{t('Choose')}</option>
              <option value="operable">{t('Operable')}</option>
              <option value="non-operable">{t('Non-operable')}</option>
            </Select>
          </Box>
        </SimpleGrid>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          <Box flex={{ md: '2' }}>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('VIN / Chassis Number')}</Text>
            <Input size="sm" borderRadius="lg" bg={inputBg} placeholder={t('17-character VIN')} value={item.vin || ''} onChange={(e) => onChange({ ...item, vin: e.target.value })} />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Weight (kg)')}</Text>
            <Input
              size="sm"
              borderRadius="lg"
              bg={inputBg}
              type="number"
              min={0}
              placeholder="1500"
              value={item.weight || ''}
              onChange={(e) => onChange({ ...item, weight: e.target.value })}
            />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Qty')}</Text>
            <NumberInput size="sm" min={1} value={item.quantity || 1} onChange={(val) => onChange({ ...item, quantity: parseInt(val, 10) })}>
              <NumberInputField borderRadius="lg" bg={inputBg} />
              <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
            </NumberInput>
          </Box>
        </SimpleGrid>
      </Stack>
    );
  }

  if (REMOVAL_CATS.includes(category)) {
    return (
      <Stack spacing={4}>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Total volume (m³)')}</Text>
            <Input size="sm" borderRadius="lg" bg={inputBg} type="number" min={0} placeholder="0"
              value={item.totalVolumeCbm ?? item.volume ?? ''}
              onChange={(e) => onChange({ ...item, totalVolumeCbm: e.target.value, volume: e.target.value })} />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Weight (kg)')}</Text>
            <Input
              size="sm"
              borderRadius="lg"
              bg={inputBg}
              type="number"
              min={0}
              placeholder="0"
              value={item.weight || ''}
              onChange={(e) => onChange({ ...item, weight: e.target.value })}
            />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Origin floor')}</Text>
            <Input size="sm" borderRadius="lg" bg={inputBg} placeholder="0" value={item.originFloor || ''} onChange={(e) => onChange({ ...item, originFloor: e.target.value })} />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Dest. floor')}</Text>
            <Input size="sm" borderRadius="lg" bg={inputBg} placeholder="0" value={item.destFloor || ''} onChange={(e) => onChange({ ...item, destFloor: e.target.value })} />
          </Box>
        </SimpleGrid>
        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={6}>
          <HStack>
            <Switch size="sm" colorScheme="brand" isChecked={!!item.hasElevatorOrigin} onChange={(e) => onChange({ ...item, hasElevatorOrigin: e.target.checked })} />
            <Text fontSize="sm">{t('Elevator at origin')}</Text>
          </HStack>
          <HStack>
            <Switch size="sm" colorScheme="brand" isChecked={!!item.hasElevatorDest} onChange={(e) => onChange({ ...item, hasElevatorDest: e.target.checked })} />
            <Text fontSize="sm">{t('Elevator at destination')}</Text>
          </HStack>
        </SimpleGrid>
      </Stack>
    );
  }

  if (PET_CATS.includes(category)) {
    return (
      <Stack spacing={4}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Pet type')}</Text>
            <Input size="sm" borderRadius="lg" bg={inputBg} placeholder={t('e.g. Dog')} value={item.petType || ''} onChange={(e) => onChange({ ...item, petType: e.target.value })} />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Breed')}</Text>
            <Input size="sm" borderRadius="lg" bg={inputBg} placeholder={t('e.g. Labrador')} value={item.breed || ''} onChange={(e) => onChange({ ...item, breed: e.target.value })} />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Weight (kg)')}</Text>
            <Input size="sm" borderRadius="lg" bg={inputBg} type="number" min={0} placeholder="0" value={item.weight || ''} onChange={(e) => onChange({ ...item, weight: e.target.value })} />
          </Box>
        </SimpleGrid>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Description')}</Text>
          <Input size="sm" borderRadius="lg" bg={inputBg} placeholder={t('Special needs or notes')} value={item.petDescription || ''} onChange={(e) => onChange({ ...item, petDescription: e.target.value })} />
        </Box>
      </Stack>
    );
  }

  // General (furniture, freight, parcels, etc.)
  return (
    <Stack spacing={4}>
      <Box>
        <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Description')}</Text>
        <Input size="sm" borderRadius="lg" bg={inputBg} placeholder={t('e.g. Wooden dining table')} value={item.itemDescription || ''} onChange={(e) => onChange({ ...item, itemDescription: e.target.value })} />
      </Box>
      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3}>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Weight (kg)')}</Text>
          <Input size="sm" borderRadius="lg" bg={inputBg} type="number" min={0} placeholder="0" value={item.weight || ''} onChange={(e) => onChange({ ...item, weight: e.target.value })} />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('L cm')}</Text>
          <Input size="sm" borderRadius="lg" bg={inputBg} type="number" min={0} placeholder="0" value={item.length || ''} onChange={(e) => onChange({ ...item, length: e.target.value })} />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('W cm')}</Text>
          <Input size="sm" borderRadius="lg" bg={inputBg} type="number" min={0} placeholder="0" value={item.width || ''} onChange={(e) => onChange({ ...item, width: e.target.value })} />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('H cm')}</Text>
          <Input size="sm" borderRadius="lg" bg={inputBg} type="number" min={0} placeholder="0" value={item.height || ''} onChange={(e) => onChange({ ...item, height: e.target.value })} />
        </Box>
        <Box>
          <Text fontSize="xs" fontWeight="700" color={labelColor} mb={1}>{t('Qty')}</Text>
          <NumberInput size="sm" min={1} value={item.quantity || 1} onChange={(val) => onChange({ ...item, quantity: parseInt(val, 10) })}>
            <NumberInputField borderRadius="lg" bg={inputBg} />
            <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
          </NumberInput>
        </Box>
      </SimpleGrid>
    </Stack>
  );
}

export default function ClientListingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const cancelRef = useRef(null);
  const { user } = useAuth();
  const { t } = useTranslation();
  const { tabListProps, tabProps } = useUnifiedTabsStyles();
  const { panelBg, panelBorder, mutedText } = useUnifiedSurfaceStyles();

  const [listing, setListing] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loadingListing, setLoadingListing] = useState(true);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [canceling, setCanceling] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const buildEditForm = useCallback((sourceListing, prefill = {}) => ({
    title: prefill.title ?? sourceListing.title ?? '',
    description: prefill.description ?? sourceListing.description ?? '',
    allowsQuotes: prefill.allowsQuotes ?? sourceListing.allowsQuotes !== false,
    clientPriceGross:
      prefill.clientPriceGross != null
        ? String(prefill.clientPriceGross)
        : sourceListing.clientPriceGross != null
          ? String(sourceListing.clientPriceGross)
          : '',
    flexiblePickup: prefill.flexiblePickup ?? sourceListing.flexiblePickup ?? !sourceListing.pickupDateFrom,
    pickupDateFrom:
      prefill.pickupDateFrom ??
      (sourceListing.pickupDateFrom ? sourceListing.pickupDateFrom.slice(0, 10) : ''),
    pickupDateTo:
      prefill.pickupDateTo ??
      (sourceListing.pickupDateTo ? sourceListing.pickupDateTo.slice(0, 10) : ''),
    flexibleDelivery: prefill.flexibleDelivery ?? sourceListing.flexibleDelivery ?? !sourceListing.deliveryDateFrom,
    deliveryDateFrom:
      prefill.deliveryDateFrom ??
      (sourceListing.deliveryDateFrom ? sourceListing.deliveryDateFrom.slice(0, 10) : ''),
    deliveryDateTo:
      prefill.deliveryDateTo ??
      (sourceListing.deliveryDateTo ? sourceListing.deliveryDateTo.slice(0, 10) : ''),
    items:
      Array.isArray(prefill.items) && prefill.items.length
        ? prefill.items
        : Array.isArray(sourceListing.items) && sourceListing.items.length
          ? sourceListing.items
          : [defaultItemFor(sourceListing.category)],
  }), []);

  const fetchListing = useCallback(async () => {
    setLoadingListing(true);
    try {
      const data = await getListingById(id);
      setListing(data?.listing ?? data);
      setError(null);
    } catch {
      setError(t('Failed to load listing.'));
    } finally {
      setLoadingListing(false);
    }
  }, [id, t]);

  const fetchQuotes = useCallback(async () => {
    setLoadingQuotes(true);
    try {
      const data = await getQuotesForListing(id);
      setQuotes(Array.isArray(data) ? data : data?.quotes ?? data?.data ?? []);
    } catch {
      setQuotes([]);
    } finally {
      setLoadingQuotes(false);
    }
  }, [id]);

  const MESSAGING_STATUSES = useMemo(() => new Set(['paid', 'planned', 'in_transit', 'pickedUp', 'delivered', 'completed']), []);

  useEffect(() => {
    fetchListing();
    fetchQuotes();
  }, [fetchListing, fetchQuotes]);

  useEffect(() => {
    if (!listing || !user?.uid || !MESSAGING_STATUSES.has(String(listing.status || ''))) return;
    getUnreadMessageCount(id, user.uid).then(setMessageUnreadCount).catch(() => {});
  }, [id, listing, user?.uid, MESSAGING_STATUSES]);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('tab') !== 'messages') return;
    if (!listing || !MESSAGING_STATUSES.has(String(listing.status || ''))) return;
    setTabIndex(2);
    searchParams.delete('tab');
    setSearchParams(searchParams, { replace: true });
  }, [listing, searchParams, setSearchParams, MESSAGING_STATUSES]);

  const handleRefresh = () => {
    fetchListing();
    fetchQuotes();
  };

  const handleCancelListing = async () => {
    setCanceling(true);
    try {
      await cancelListing(id);
      toast({ title: t('Listing canceled'), status: 'success', duration: 2500, isClosable: true });
      navigate('/my-listings');
    } catch {
      toast({ title: t('Failed to cancel listing'), status: 'error', duration: 3000, isClosable: true });
    } finally {
      setCanceling(false);
      setShowCancelDialog(false);
    }
  };

  const handleOpenEdit = useCallback((prefill = {}) => {
    setEditForm(buildEditForm(listing, prefill));
    setShowEditModal(true);
  }, [buildEditForm, listing]);

  useEffect(() => {
    if (!listing?.id && !listing?._id) {
      return;
    }

    const aiPrefill = consumeClientListingEditPrefill(id);
    if (aiPrefill) {
      handleOpenEdit(aiPrefill);
    }
  }, [handleOpenEdit, id, listing]);

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description,
        allowsQuotes: editForm.allowsQuotes,
        clientPriceGross: editForm.clientPriceGross === '' ? null : Number(editForm.clientPriceGross),
        flexiblePickup: editForm.flexiblePickup,
        pickupDateFrom: editForm.flexiblePickup ? null : editForm.pickupDateFrom || null,
        pickupDateTo: editForm.flexiblePickup ? null : editForm.pickupDateTo || null,
        flexibleDelivery: editForm.flexibleDelivery,
        deliveryDateFrom: editForm.flexibleDelivery ? null : editForm.deliveryDateFrom || null,
        deliveryDateTo: editForm.flexibleDelivery ? null : editForm.deliveryDateTo || null,
        items: editForm.items,
      };
      await updateListing(id, payload);
      toast({ title: t('Listing updated'), status: 'success', duration: 2500, isClosable: true });
      setShowEditModal(false);
      fetchListing();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || t('Failed to update listing'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await completeListingService(id);
      toast({ title: t('Transport marked as complete'), status: 'success', duration: 2500, isClosable: true });
      fetchListing();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || t('Failed to complete listing'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setCompleting(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await publishListingDraft(id);
      toast({ title: t('Listing published'), status: 'success', duration: 2500, isClosable: true });
      fetchListing();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || t('Failed to publish listing'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setPublishing(false);
    }
  };

  if (loadingListing) {
    return (
      <PageShell>
        <UnifiedLoadingState label={t('Loading listing details...')} minH="50vh" />
      </PageShell>
    );
  }

  if (error || !listing) {
    return (
      <PageShell>
        <UnifiedSurfaceCard p={6}>
          <Text color="red.500">{error || t('Listing not found.')}</Text>
        </UnifiedSurfaceCard>
      </PageShell>
    );
  }

  const listingTitle =
    listing.title ||
    (listing.route ? `${listing.route?.from || ''} → ${listing.route?.to || ''}` : t('Listing')) ||
    t('Listing');
  const listingNumber =
    listing.reference ||
    listing.humanId ||
    (listing._id || listing.id || '').toString().slice(-6).toUpperCase();
  const expiresAt = listing.expiresAt || listing.validUntil;
  const remaining = daysLeft(expiresAt);
  const acceptedQuote =
    quotes.find((quote) => String(quote.status || quote._status || '').toLowerCase() === 'accepted') || null;
  const quoteCount = quotes.length;
  const bestQuoteAmount = getQuoteAmounts(quotes);
  const bestQuote = bestQuoteAmount.length
    ? Math.min(...bestQuoteAmount)
    : listing.clientPriceGross != null
      ? Number(listing.clientPriceGross)
      : null;
  const itemCount = getItemCount(listing.items);
  const routeLabel = [listing.fromCity, listing.toCity].filter(Boolean).join(' → ') || t('Route pending');
  const canCancel = !['canceled', 'completed'].includes(String(listing.status || '').toLowerCase());
  const canEdit = ['draft', 'open', 'has_quotes'].includes(String(listing.status || '').toLowerCase());
  const isDraft = String(listing.status || '').toLowerCase() === 'draft';

  return (
    <PageShell>
      <Box w="100%" mx="auto">
        <Flex align="center" justify="space-between" mb={4}>
          <Button size="sm" variant="ghost" leftIcon={<Icon as={MdArrowBack} />} onClick={() => navigate('/my-listings')}>
            {t('Back to listings')}
          </Button>
          <HStack spacing={2}>
            <AiAssistantDrawer
              role="client"
              screenContext={{
                routeKey: 'client.listings.detail',
                listingId: id,
                entityType: 'listing',
                entityId: id,
                role: 'client',
                surface: 'listing-detail',
              }}
              buttonLabel={t('Ask AI')}
              subtitle={t('Ask for quote comparisons or a prepared next step from this shipment screen.')}
              onHandoff={(handoffRoute) => {
                if (handoffRoute?.routeKey === 'client.listings.edit' && String(handoffRoute?.params?.listingId || '') === String(id)) {
                  handleOpenEdit(handoffRoute?.params?.prefill || {});
                  return true;
                }

                return false;
              }}
            />
            {canEdit ? (
              <Button
                variant="outline"
                colorScheme="brand"
                borderRadius="full"
                size="sm"
                leftIcon={<Icon as={MdEdit} />}
                onClick={handleOpenEdit}
              >
                {t('Edit listing')}
              </Button>
            ) : null}
            {isDraft ? (
              <Button
                colorScheme="blue"
                borderRadius="full"
                size="sm"
                onClick={handlePublish}
                isLoading={publishing}
                loadingText={t('Publishing...')}
              >
                {t('Publish listing')}
              </Button>
            ) : null}
            {canCancel ? (
              <Button
                variant="outline"
                colorScheme="red"
                borderRadius="full"
                size="sm"
                leftIcon={<Icon as={MdDeleteOutline} />}
                onClick={() => setShowCancelDialog(true)}
              >
                {t('Cancel listing')}
              </Button>
            ) : null}
            <IconButton
              icon={<Icon as={MdRefresh} />}
              aria-label={t('Refresh')}
              variant="ghost"
              size="sm"
              borderRadius="xl"
              isLoading={loadingListing || loadingQuotes}
              onClick={handleRefresh}
            />
          </HStack>
        </Flex>

        <UnifiedSurfaceCard
          p={{ base: 5, md: 6 }}
          mb={6}
          bg={panelBg}
          borderColor={panelBorder}
        >
          <Box minW={0}>
            <HStack spacing={2} flexWrap="wrap" mb={2}>
              <Badge colorScheme="blue" borderRadius="full" px={3} py={1} textTransform="capitalize">
                {listing.category ? listing.category.replace(/_/g, ' ') : t('Listing')}
              </Badge>
              {listing.allowsQuotes === false ? (
                <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
                  {t('Fixed price')}
                </Badge>
              ) : null}
              <StatusBadge status={listing.status} />
              {listing.clientPriceGross != null ? (
                <Badge colorScheme="green" borderRadius="full" px={3} py={1}>
                  {formatMoney(listing.clientPriceGross)}
                </Badge>
              ) : null}
              {remaining != null ? (
                <Badge
                  colorScheme={remaining > 0 ? 'blue' : 'red'}
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  {remaining > 0 ? t('{{count}} day(s) left', { count: remaining }) : t('Expired')}
                </Badge>
              ) : null}
            </HStack>

            <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="900" color="navy.800" _dark={{ color: 'white' }}>
              {listingTitle}
            </Text>

            <HStack spacing={3} mt={2} flexWrap="wrap">
              <Text fontSize="sm" color={mutedText} fontWeight="700">
                #{listingNumber}
              </Text>
              <Text fontSize="sm" color={mutedText}>
                {routeLabel}
              </Text>
              {listing.createdAt ? (
                <Text fontSize="sm" color={mutedText}>
                  {t('Listed')} {formatDate(listing.createdAt)}
                </Text>
              ) : null}
            </HStack>
          </Box>

          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mt={5}>
            <MetricChip label={t('Quotes')} value={`${quoteCount}`} icon={MdPayments} />
            <MetricChip label={t('Items')} value={`${itemCount}`} icon={MdOutlineInventory2} />
            <MetricChip label={t('Best offer')} value={bestQuote != null ? formatMoney(bestQuote) : '—'} icon={MdCheckCircle} />
            <MetricChip label={t('Stage')} value={getTransportStage(listing.status)} icon={MdRoute} />
          </SimpleGrid>
        </UnifiedSurfaceCard>

        <Tabs index={tabIndex} onChange={setTabIndex} variant="unstyled">
          <Box
            mb={6}
            bg={panelBg}
            border="1px solid"
            borderColor={panelBorder}
            borderRadius="18px"
            px={{ base: 3, md: 4 }}
            py={2}
            backdropFilter="blur(10px)"
          >
            <TabList {...tabListProps}>
              <Tab {...tabProps}>{t('Details')}</Tab>
              <Tab {...tabProps}>
                {t('Quotes')}
                {quoteCount > 0 ? (
                  <Box
                    as="span"
                    ml={2}
                    bg={ACCENT}
                    color="white"
                    borderRadius="full"
                    px={2}
                    py={0.5}
                    fontSize="10px"
                    fontWeight="900"
                    lineHeight="1.4"
                  >
                    {quoteCount}
                  </Box>
                ) : null}
              </Tab>
              {['paid', 'planned', 'in_transit', 'pickedUp', 'delivered', 'completed'].includes(String(listing.status || '')) ? (
                <Tab {...tabProps}>
                  {t('Messages')}
                  {messageUnreadCount > 0 ? (
                    <Box
                      as="span"
                      ml={2}
                      w="8px"
                      h="8px"
                      bg={ACCENT}
                      borderRadius="full"
                      display="inline-block"
                    />
                  ) : null}
                </Tab>
              ) : null}
            </TabList>
          </Box>

          <TabPanels>
            <TabPanel p={0}>
              <Stack spacing={6}>
                <DetailsTab
                  listing={listing}
                  quotes={quotes}
                  acceptedQuote={acceptedQuote}
                  onPaymentComplete={handleRefresh}
                  onComplete={handleComplete}
                  completing={completing}
                />
                <ClientImageManager listing={listing} onImagesUpdated={fetchListing} />
              </Stack>
            </TabPanel>

            <TabPanel p={0}>
              {loadingQuotes ? (
                <UnifiedLoadingState label={t('Loading quotes...')} minH="220px" />
              ) : (
                <Stack spacing={6}>
                  <QuotesTab listing={listing} quotes={quotes} listingId={id} onRefresh={handleRefresh} />
                </Stack>
              )}
            </TabPanel>

            {['paid', 'planned', 'in_transit', 'pickedUp', 'delivered', 'completed'].includes(String(listing.status || '')) ? (
              <TabPanel p={0}>
                <ListingMessagesPanel listingId={id} currentUserId={user?.uid} role="client" onUnreadCountChange={setMessageUnreadCount} />
              </TabPanel>
            ) : null}
          </TabPanels>
        </Tabs>
      </Box>

      <AlertDialog
        isOpen={showCancelDialog}
        leastDestructiveRef={cancelRef}
        onClose={() => (canceling ? null : setShowCancelDialog(false))}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent borderRadius="2xl">
          <AlertDialogHeader fontSize="lg" fontWeight="800">
            {t('Cancel listing')}
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={2}>
              {t('This will soft-cancel the listing and move it to your archive.')}
            </Text>
            <Text fontWeight="700">{listingTitle}</Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={() => setShowCancelDialog(false)} isDisabled={canceling}>
              {t('Keep listing')}
            </Button>
            <Button
              colorScheme="red"
              ml={3}
              onClick={handleCancelListing}
              isLoading={canceling}
              loadingText={t('Canceling...')}
            >
              {t('Cancel listing')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Modal isOpen={showEditModal} onClose={() => saving ? null : setShowEditModal(false)} size="3xl" isCentered scrollBehavior="outside">
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader fontSize="lg" fontWeight="800">{t('Edit listing')}</ModalHeader>
          <ModalCloseButton isDisabled={saving} />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="700">{t('Title')}</FormLabel>
                <Input
                  value={editForm.title || ''}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                  borderRadius="xl"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="700">{t('Description')}</FormLabel>
                <Textarea
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                  borderRadius="xl"
                  resize="vertical"
                  minH="96px"
                />
              </FormControl>
              <FormControl>
                <HStack mb={3} spacing={3}>
                  <Switch
                    colorScheme="brand"
                    isChecked={editForm.allowsQuotes !== false}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, allowsQuotes: e.target.checked }))}
                  />
                  <FormLabel fontSize="sm" fontWeight="600" mb={0} cursor="pointer">
                    {t('Allow partner quotes')}
                  </FormLabel>
                </HStack>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={editForm.clientPriceGross || ''}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, clientPriceGross: e.target.value }))}
                  borderRadius="xl"
                  placeholder={t('Client price in EUR')}
                />
                <Text mt={2} fontSize="xs" color="gray.500">
                  {editForm.allowsQuotes === false
                    ? t('Required for fixed-price listings. Partners accept this amount directly.')
                    : t('Optional visible price shown to partners as your target budget.')}
                </Text>
              </FormControl>
              <FormControl>
                <HStack mb={3} spacing={3}>
                  <Switch
                    colorScheme="brand"
                    isChecked={!editForm.flexiblePickup}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, flexiblePickup: !e.target.checked }))}
                  />
                  <FormLabel fontSize="sm" fontWeight="600" mb={0} cursor="pointer">
                    {t('Specify pickup dates')}
                  </FormLabel>
                </HStack>
                {editForm.flexiblePickup ? null : (
                  <RangeDatepicker
                    selectedDates={[
                      editForm.pickupDateFrom ? new Date(editForm.pickupDateFrom) : null,
                      editForm.pickupDateTo ? new Date(editForm.pickupDateTo) : null,
                    ].filter(Boolean)}
                    onDateChange={(dates) => {
                      if (dates.length === 2) {
                        setEditForm((prev) => ({ ...prev, pickupDateFrom: format(dates[0], 'yyyy-MM-dd'), pickupDateTo: format(dates[1], 'yyyy-MM-dd') }));
                      } else if (dates.length === 1) {
                        setEditForm((prev) => ({ ...prev, pickupDateFrom: format(dates[0], 'yyyy-MM-dd'), pickupDateTo: null }));
                      }
                    }}
                    propsConfigs={{
                      inputProps: { size: 'sm', borderRadius: 'lg' },
                      popoverCompProps: { zIndex: 9999 },
                      dayOfMonthBtnProps: {
                        defaultBtnProps: { _hover: { bg: 'brand.500', color: 'white' } },
                        selectedBtnProps: { bg: 'brand.500', color: 'white' },
                      },
                    }}
                    minDate={startOfToday()}
                  />
                )}
              </FormControl>
              <FormControl>
                <HStack mb={3} spacing={3}>
                  <Switch
                    colorScheme="brand"
                    isChecked={!editForm.flexibleDelivery}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, flexibleDelivery: !e.target.checked }))}
                  />
                  <FormLabel fontSize="sm" fontWeight="600" mb={0} cursor="pointer">
                    {t('Specify delivery dates')}
                  </FormLabel>
                </HStack>
                {editForm.flexibleDelivery ? null : (
                  <RangeDatepicker
                    selectedDates={[
                      editForm.deliveryDateFrom ? new Date(editForm.deliveryDateFrom) : null,
                      editForm.deliveryDateTo ? new Date(editForm.deliveryDateTo) : null,
                    ].filter(Boolean)}
                    onDateChange={(dates) => {
                      if (dates.length === 2) {
                        setEditForm((prev) => ({ ...prev, deliveryDateFrom: format(dates[0], 'yyyy-MM-dd'), deliveryDateTo: format(dates[1], 'yyyy-MM-dd') }));
                      } else if (dates.length === 1) {
                        setEditForm((prev) => ({ ...prev, deliveryDateFrom: format(dates[0], 'yyyy-MM-dd'), deliveryDateTo: null }));
                      }
                    }}
                    propsConfigs={{
                      inputProps: { size: 'sm', borderRadius: 'lg' },
                      popoverCompProps: { zIndex: 9999 },
                      dayOfMonthBtnProps: {
                        defaultBtnProps: { _hover: { bg: 'brand.500', color: 'white' } },
                        selectedBtnProps: { bg: 'brand.500', color: 'white' },
                      },
                    }}
                    minDate={startOfToday()}
                  />
                )}
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="700" mb={3}>{t('Items')}</FormLabel>
                <Stack spacing={3}>
                  {(editForm.items || []).map((item, idx) => (
                    <EditItemCard
                      key={idx}
                      category={listing.category}
                      item={item}
                      idx={idx}
                      totalItems={(editForm.items || []).length}
                      onRemove={() => setEditForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }))}
                      onItemChange={(updated) => setEditForm((prev) => {
                        const next = [...prev.items];
                        next[idx] = updated;
                        return { ...prev, items: next };
                      })}
                    />
                  ))}
                </Stack>
                <Button
                  mt={3}
                  size="sm"
                  variant="link"
                  colorScheme="brand"
                  leftIcon={<Icon as={MdAdd} />}
                  onClick={() => setEditForm((prev) => ({ ...prev, items: [...(prev.items || []), defaultItemFor(listing.category)] }))}
                >
                  {t('Add another item')}
                </Button>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button borderRadius="xl" onClick={() => setShowEditModal(false)} isDisabled={saving}>
              {t('Cancel')}
            </Button>
            <Button colorScheme="brand" borderRadius="xl" isLoading={saving} loadingText={t('Saving...')} onClick={handleSaveEdit}>
              {t('Save changes')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageShell>
  );
}
